import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Settings, AlertTriangle, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SystemSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingModeEnabled, setTestingModeEnabled] = useState(false);
  const [testingEmail, setTestingEmail] = useState('');

  useEffect(() => {
    axios.get(`${API}/settings/testing-mode`, { withCredentials: true })
      .then(r => {
        setTestingModeEnabled(!!r.data.testing_mode_enabled);
        setTestingEmail(r.data.testing_email_address || '');
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (testingModeEnabled && !testingEmail.trim()) {
      toast.error('Testing email address is required when testing mode is enabled.');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.patch(
        `${API}/settings/testing-mode`,
        {
          testing_mode_enabled: testingModeEnabled,
          testing_email_address: testingEmail.trim() || null,
        },
        { withCredentials: true }
      );
      setTestingModeEnabled(!!r.data.testing_mode_enabled);
      setTestingEmail(r.data.testing_email_address || '');
      toast.success('Settings saved.');
      // Poke the layout so the red banner shows/hides instantly.
      window.dispatchEvent(new Event('testing-mode-changed'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading settings…</div>;

  return (
    <div className="space-y-6 fade-in" data-testid="system-settings-page">
      <div className="flex items-center gap-3">
        <Settings className="h-7 w-7" style={{ color: '#2D4A3E' }} />
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
          System Settings
        </h1>
      </div>

      <Card className="p-6 space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Testing Mode</h2>
          <p className="text-sm text-muted-foreground">
            When enabled, <strong>every outbound email</strong> is redirected to the address below —
            regardless of the per-send LIVE toggle. Use this to safely rehearse workflows without
            reaching real recipients.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <Switch
            id="testing-mode-toggle"
            checked={testingModeEnabled}
            onCheckedChange={setTestingModeEnabled}
            data-testid="testing-mode-toggle"
          />
          <Label htmlFor="testing-mode-toggle" className="cursor-pointer">
            {testingModeEnabled ? (
              <span className="text-red-600 font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Testing mode is ON
              </span>
            ) : (
              <span className="text-muted-foreground">Testing mode is off</span>
            )}
          </Label>
        </div>

        <div>
          <Label htmlFor="testing-email">Testing email address</Label>
          <Input
            id="testing-email"
            type="email"
            value={testingEmail}
            onChange={(e) => setTestingEmail(e.target.value)}
            placeholder="e.g. testing@customcv.co.nz"
            data-testid="testing-email-input"
            className="mt-1 max-w-md"
          />
          <p className="text-xs text-muted-foreground mt-1">
            All outbound mail will be redirected here while Testing Mode is on.
          </p>
        </div>

        <div>
          <Button
            className="btn-primary"
            onClick={save}
            disabled={saving}
            data-testid="testing-mode-save-btn"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </Card>

      <HelpContentEditor />
      <GrandfatherEmailVerification />
      <BackfillCountryToNZ />
      <BackfillInternationalWarmups />
    </div>
  );
}

/**
 * Super-only one-click backfill for the International warm-up level
 * (added Feb 2026 for WorldSkate Figures grades). Copies the stored
 * Nationals value into International anywhere International is still
 * empty — idempotent, safe to click again. Pairs with the lazy
 * read-time fallback in ``GET /api/event-timings`` so forms stay
 * correct even if the organiser forgets to click this button.
 */
function BackfillInternationalWarmups() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/event-timings/backfill-international`, {}, { withCredentials: true });
      setResult(r.data);
      const docs = r.data?.docs_updated || 0;
      toast.success(
        docs > 0
          ? `Updated ${docs} Figures timing doc${docs === 1 ? '' : 's'}.`
          : 'Every Figures timing row already has International values — nothing to do.'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="backfill-international-card">
      <div>
        <h2 className="text-xl font-semibold mb-1">Figures — Backfill International Warm-up Timings</h2>
        <p className="text-sm text-muted-foreground">
          One-time step after the International warm-up level rollout. Copies the Nationals
          warm-up (and add-for-extra) onto the new International slot for any WorldSkate Figures
          row that doesn&apos;t carry an International value yet. Rows that already have an
          explicit International value are left alone. Idempotent — safe to run again.
        </p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          className="btn-primary"
          onClick={run}
          disabled={running}
          data-testid="backfill-international-btn"
        >
          {running ? 'Running…' : 'Backfill International warm-up timings'}
        </Button>
        {result && (
          <span className="text-sm text-muted-foreground">
            Scanned <strong>{result.docs_scanned}</strong> · Updated <strong>{result.docs_updated}</strong> doc{result.docs_updated === 1 ? '' : 's'} · {result.rows_updated} row{result.rows_updated === 1 ? '' : 's'}
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * Super-only one-click backfill for the country rollout. Mirrors the
 * email-verification grandfather button — idempotent, safe to click
 * repeatedly (only touches rows without a country).
 */
function BackfillCountryToNZ() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/countries/backfill-nz`, {}, { withCredentials: true });
      setResult(r.data);
      const total = ['users', 'clubs', 'venues', 'key_contacts']
        .reduce((n, k) => n + (r.data?.[k]?.modified || 0), 0);
      toast.success(
        total > 0
          ? `${total} record${total === 1 ? '' : 's'} updated to New Zealand.`
          : 'Everything already has a country — nothing to do.'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="backfill-country-card">
      <div>
        <h2 className="text-xl font-semibold mb-1">Country — Backfill Missing Values to New Zealand</h2>
        <p className="text-sm text-muted-foreground">
          One-time step after the Country field rollout. Stamps every user, club, venue
          and key contact missing a country as New Zealand. Idempotent — safe to run
          again (only touches rows that don&apos;t have a country yet).
        </p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          className="btn-primary"
          onClick={run}
          disabled={running}
          data-testid="backfill-country-btn"
        >
          {running ? 'Running…' : 'Backfill missing countries to New Zealand'}
        </Button>
        {result && (
          <span className="text-sm text-muted-foreground">
            {['users', 'clubs', 'venues', 'key_contacts'].map((k) => (
              <span key={k} className="mr-3">
                {k}: <strong>{result[k]?.modified ?? 0}</strong>/{result[k]?.matched ?? 0}
              </span>
            ))}
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * One-shot idempotent grandfather step for the email-verification
 * rollout. Marks every user who isn't already verified as verified.
 * Safe to click on both preview and production — only touches rows
 * that need it.
 */
function GrandfatherEmailVerification() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/auth/backfill-verified`, {}, { withCredentials: true });
      setResult(r.data);
      toast.success(
        r.data.modified > 0
          ? `${r.data.modified} existing user${r.data.modified === 1 ? '' : 's'} marked as verified.`
          : 'Everyone is already verified — nothing to do.'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="grandfather-verified-card">
      <div>
        <h2 className="text-xl font-semibold mb-1">Email Verification — Grandfather Existing Users</h2>
        <p className="text-sm text-muted-foreground">
          One-time step after enabling the Email Verification feature. Marks every user
          created before the rollout as already-verified so they aren&apos;t locked out on
          their next login. Safe to run multiple times — only touches users who aren&apos;t
          already verified.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Button
          className="btn-primary"
          onClick={run}
          disabled={running}
          data-testid="grandfather-verified-btn"
        >
          {running ? 'Running…' : 'Grandfather all existing users as verified'}
        </Button>
        {result && (
          <span className="text-sm text-muted-foreground">
            Matched: <strong>{result.matched}</strong> · Modified:{' '}
            <strong>{result.modified}</strong>
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * Super-only editor for the six "Getting Started" help topics shown on
 * the Dashboard. Each topic is a collapsible card; edits are saved per
 * topic so a mistake in one row can't clobber the others.
 */
function HelpContentEditor() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    axios.get(`${API}/help-content`, { withCredentials: true })
      .then(r => setTopics(r.data || []))
      .catch(() => toast.error('Failed to load help content'))
      .finally(() => setLoading(false));
  }, []);

  const patchTopic = (key, patch) => {
    setTopics(topics.map(t => t.key === key ? { ...t, ...patch } : t));
  };

  const patchLink = (key, idx, patch) => {
    const t = topics.find(x => x.key === key);
    if (!t) return;
    const links = [...(t.related_links || [])];
    links[idx] = { ...links[idx], ...patch };
    patchTopic(key, { related_links: links });
  };

  const addLink = (key) => {
    const t = topics.find(x => x.key === key);
    if (!t) return;
    patchTopic(key, { related_links: [...(t.related_links || []), { label: '', url: '' }] });
  };

  const removeLink = (key, idx) => {
    const t = topics.find(x => x.key === key);
    if (!t) return;
    const links = [...(t.related_links || [])];
    links.splice(idx, 1);
    patchTopic(key, { related_links: links });
  };

  const saveTopic = async (topic) => {
    if (!topic.title.trim() || !topic.intro.trim()) {
      toast.error('Title and intro are required.');
      return;
    }
    setSavingKey(topic.key);
    try {
      const r = await axios.put(
        `${API}/help-content/${topic.key}`,
        {
          title: topic.title,
          intro: topic.intro,
          body: topic.body,
          related_links: (topic.related_links || []).filter(l => l.label.trim() && l.url.trim()),
        },
        { withCredentials: true },
      );
      setTopics(topics.map(t => t.key === topic.key ? r.data : t));
      toast.success(`Saved "${r.data.title}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return null;

  return (
    <Card className="p-6 space-y-4" data-testid="help-content-editor">
      <div>
        <h2 className="text-xl font-semibold mb-1">Getting Started Help Content</h2>
        <p className="text-sm text-muted-foreground">
          Edit the six help pages that appear when a user clicks a Getting Started tile on the
          Dashboard. Body supports blank-line paragraphs and lines beginning with <code>- </code>
          become bullet points.
        </p>
      </div>

      <div className="space-y-2">
        {topics.map(topic => {
          const isOpen = openKey === topic.key;
          return (
            <div key={topic.key} className="border rounded-lg" data-testid={`help-topic-${topic.key}`}>
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? null : topic.key)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-stone-50"
              >
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span className="font-medium">{topic.title}</span>
                <span className="text-xs text-muted-foreground ml-auto">{topic.key}</span>
              </button>
              {isOpen && (
                <div className="p-4 border-t space-y-3">
                  <div>
                    <Label>Title *</Label>
                    <Input
                      value={topic.title}
                      onChange={(e) => patchTopic(topic.key, { title: e.target.value })}
                      data-testid={`help-title-${topic.key}`}
                    />
                  </div>
                  <div>
                    <Label>Intro *</Label>
                    <Input
                      value={topic.intro}
                      onChange={(e) => patchTopic(topic.key, { intro: e.target.value })}
                      data-testid={`help-intro-${topic.key}`}
                    />
                  </div>
                  <div>
                    <Label>Body</Label>
                    <Textarea
                      value={topic.body}
                      rows={8}
                      onChange={(e) => patchTopic(topic.key, { body: e.target.value })}
                      data-testid={`help-body-${topic.key}`}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Blank line = new paragraph. Lines beginning with <code>- </code> become bullets.
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Related links</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addLink(topic.key)}
                        data-testid={`help-add-link-${topic.key}`}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add link
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(topic.related_links || []).map((link, idx) => (
                        <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                          <Input
                            value={link.label}
                            placeholder="Label (e.g. Manage Venues)"
                            onChange={(e) => patchLink(topic.key, idx, { label: e.target.value })}
                          />
                          <Input
                            value={link.url}
                            placeholder="/venues"
                            onChange={(e) => patchLink(topic.key, idx, { url: e.target.value })}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLink(topic.key, idx)}
                            aria-label="Remove link"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      ))}
                      {(topic.related_links || []).length === 0 && (
                        <p className="text-xs text-muted-foreground">No related links yet.</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Button
                      className="btn-primary"
                      onClick={() => saveTopic(topic)}
                      disabled={savingKey === topic.key}
                      data-testid={`help-save-${topic.key}`}
                    >
                      {savingKey === topic.key ? 'Saving…' : 'Save this topic'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
